import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// `default` (coral) is the single primary action per screen; everything else is
// `outline` (white pill with a border) or `ink` for strong-but-not-primary actions.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:     "bg-waves-koraal text-white hover:bg-waves-koraal-hover disabled:bg-waves-koraal-disabled",
        ink:         "bg-waves-ink text-white hover:bg-waves-ink-deep disabled:opacity-50",
        outline:     "border border-waves-line bg-white text-waves-ink hover:bg-waves-mist disabled:opacity-50",
        destructive: "border border-status-alert-bg bg-white text-status-alert-fg hover:bg-status-alert-bg disabled:opacity-50",
        ghost:       "text-waves-ink hover:bg-waves-subtle disabled:opacity-50",
        link:        "text-waves-ink underline-offset-4 hover:underline disabled:opacity-50",
      },
      size: {
        default: "h-11 px-5 text-sm",
        sm:      "h-9 px-4 text-[13px]",
        lg:      "h-14 px-8 text-base",
        icon:    "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={asChild ? undefined : (type ?? "button")}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
